import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { historyStatusColor } from "@lib/helpers/ColorHelper";
import { Tag } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { UseMutationProps } from "@lib/types/Api";
import TableIdButton from "@lib/components/MiniComponents/Buttons/TableIdButton";
import { shortenString } from "@lib/helpers/StringHelper";
import { ColumnConfig, generateColumns } from "@lib/helpers/TableHelper";
import CustomerName from "@lib/components/MiniComponents/Others/CustomerName";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { TableColumnsProps } from "@lib/types/General";
import { TbWorldSearch } from "react-icons/tb";
import WalletAddressText from "@lib/components/MiniComponents/Others/WalletAddress";
import { CoinPriceTextFormat } from "@lib/components/MiniComponents/Others";
import { AuthRoles } from "@lib/types";
import {
  AIBlockchainPaymentHistory,
  AIBlockchainPaymentHistoryStatus,
} from "@lib/types/Wallet";

type Props<T> = TableColumnsProps<T> & {
  deleteQuery: UseMutationProps<T>;
};

const getColumns = <T extends AIBlockchainPaymentHistory>(
  props: Props<T>,
): ColumnsType<T> => {
  const { messages, deleteQuery, detailContext, userRole } = props;

  const columnsConfig: ColumnConfig<T>[] = [
    {
      title: "ID",
      dataIndex: "id",
      width: "160px",
      render: (id) => (
        <TableIdButton text={`${shortenString(id)}`} textToBeCopied={id} />
      ),
    },

    {
      title: "common.walletAddress",
      dataIndex: "walletAddress.address",
      width: "230px",
      render: (_, r) => (
        <WalletAddressText
          walletAddress={r?.walletAddress}
          showNetwork={true}
          shortenAddress={20}
        />
      ),
      filterType: "search",
    },

    {
      title: "common.amount",
      dataIndex: "extraData.scanned.lastTxAmount",
      width: "185px",
      align: "right",
      render: (value, r) => (
        <CoinPriceTextFormat
          amount={r?.extraData?.scanned?.lastTxAmount}
          currency={r?.network}
          showCurrency={r?.network}
          showCurrencyImage
        />
      ),
      filterType: "search",
    },

    {
      title: "common.customerName",
      dataIndex: "customer.id",
      key: "customer.name",
      width: "200px",
      render: (v, r) => <CustomerName customer={r?.customer} link />,
      hidden: !!detailContext,
    },

    {
      title: "common.status",
      dataIndex: "status",
      width: "150px",
      render: (value) => (
        <Tag color={historyStatusColor(value)}>
          {(messages["enums.billingHistory"][value] as string) || value}
        </Tag>
      ),
      filterType: "dropdown",
      filters: Object.values(AIBlockchainPaymentHistoryStatus).map((value) => ({
        text: messages["enums.billingHistory"][value] || value,
        value: value,
      })),
    },

    {
      title: "common.createdAt",
      dataIndex: "createdAt",
      width: "180px",
      render: (d) => dayjs(d).fromNow(),
    },
    {
      title: "common.createdBy",
      dataIndex: "createdBy",
      width: "180px",
      filterType: "dropdown",
    },
    {
      title: "common.actions",
      dataIndex: "actions",
      className: "order-table-action",
      fixed: "right",
      width: "160px",
      render: (_, record) => (
        <StyledFlex className="actions">
          <ActionButton
            type="link"
            targetBlank
            icon={<TbWorldSearch />}
            href={record?.walletAddress?.extraData?.viewUrl || null}
            disabled={!record?.walletAddress?.extraData?.viewUrl}
            label={messages["common.blockChain"] as string}
            showBorder
          />

          {userRole === AuthRoles.Admin && (
            <ActionButton
              type="delete"
              onClick={() => deleteQuery.mutate(record)}
              noLabel
              itemName="sidebar.blockchainPaymentRequest"
              disabled={
                record.status == AIBlockchainPaymentHistoryStatus.Success ||
                deleteQuery.isPending
              }
            />
          )}
        </StyledFlex>
      ),
    },
  ];

  return generateColumns(props, columnsConfig);
};

export default getColumns;
